/**
 * Venda na Rota — dados da tela: consultas com cópia neste aparelho (por empresa) para
 * funcionar com conexão instável, situação da conexão, saldo da carga, vendas guardadas e o
 * rascunho da venda (nesta guia; recarregar a página não perde o que foi digitado). O app que
 * hospeda escolhe se as cópias e o rascunho ficam no aparelho ou só na memória (RotaHost).
 *
 * As cópias são apagadas quando o usuário sai (clearRouteDeviceData). Nada sensível é guardado
 * além do que o próprio usuário já vê na tela; nenhum segredo.
 */
import {
  convertToBase,
  type FiscalPreview,
  type LoadDetail,
  type Party,
  type Product,
  type RouteSaleInput,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { apiFetch, getActiveCompanyId, newIdempotencyKey } from "@cafegestor/client";
import { ROUTE_CACHE_PREFIX, type RouteIntent } from "@cafegestor/client/offline";
import { useRotaHost } from "./host";
import type { CartLine, DocumentChoice, PendingItems } from "./rota-lib";

/**
 * Consulta com cópia neste aparelho (por empresa): sem conexão, usa a última lista baixada.
 * Com `deviceData: "memoria"` (CaféGestor Vendedor V1), nada é gravado no aparelho: a lista
 * fica só na memória enquanto o app está aberto (dados de clientes nunca vão para o
 * localStorage sem proteção).
 */
export function useCached<T>(companyId: string | null, name: string, path: string | null) {
  const persist = (useRotaHost().deviceData ?? "aparelho") === "aparelho";
  const storeKey = `${ROUTE_CACHE_PREFIX}${companyId ?? "-"}.${name}`;
  return useQuery({
    queryKey: ["rota", "cache", companyId, name, path],
    enabled: companyId !== null && path !== null,
    queryFn: async () => {
      const data = await apiFetch<T>(path!);
      if (persist) {
        try {
          localStorage.setItem(storeKey, JSON.stringify(data));
        } catch {
          /* sem armazenamento */
        }
      }
      return data;
    },
    initialData: () => {
      if (!persist) return undefined;
      try {
        const raw = localStorage.getItem(storeKey);
        return raw ? (JSON.parse(raw) as T) : undefined;
      } catch {
        return undefined;
      }
    },
    initialDataUpdatedAt: 0,
    staleTime: 60_000,
    retry: false,
  });
}

export function useOnline() {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener("online", cb);
      window.addEventListener("offline", cb);
      return () => {
        window.removeEventListener("online", cb);
        window.removeEventListener("offline", cb);
      };
    },
    () => navigator.onLine,
    () => true,
  );
}

/** Detalhe da carga (saldo por produto: carregado, vendido, restante), com cópia no aparelho. */
export const useLoadDetail = (companyId: string | null, loadId: string) =>
  useCached<LoadDetail>(companyId, `carga.${loadId || "-"}`, loadId ? `/cargas/${loadId}` : null);

/**
 * Itens das vendas guardadas neste aparelho para a carga e ainda não registradas: contam no
 * saldo disponível (para não vender o mesmo produto duas vezes sem conexão).
 */
export function pendingItemsOf(
  intents: RouteIntent[],
  loadId: string,
  products: Pick<Product, "id" | "baseUnitId" | "packagings">[],
): PendingItems {
  const out: PendingItems = [];
  for (const i of intents) {
    if (i.state === "concluida" || i.body.loadId !== loadId) continue;
    for (const it of i.body.items) {
      const p = products.find((x) => x.id === it.productId);
      const factor =
        !p || it.unitId === p.baseUnitId
          ? "1"
          : (p.packagings?.find((pk) => pk.unitId === it.unitId)?.factor ?? "1");
      out.push({ productId: it.productId, quantityBase: convertToBase(it.quantity, factor) });
    }
  }
  return out;
}

// ------------------------------------------------------------------ rascunho da venda
export type RouteDraft = {
  loadId: string;
  customer: Party | null;
  lines: CartLine[];
  paymentMethodId: string;
  payment: { e2e: string; auth: string };
  document: DocumentChoice;
  step: number;
};

export const EMPTY_DRAFT: RouteDraft = {
  loadId: "",
  customer: null,
  lines: [],
  paymentMethodId: "",
  payment: { e2e: "", auth: "" },
  document: "pedido",
  step: 0,
};

const DRAFT_KEY = "cafegestor.rota.rascunho";

export type DraftStore = {
  get(): RouteDraft | null;
  set(value: RouteDraft): void;
  clear(): void;
};

/** Rascunho guardado nesta guia do navegador, por empresa (outra empresa ativa começa limpa). */
export const routeDraft: DraftStore = {
  get() {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const s = JSON.parse(raw) as { companyId: string | null; value: RouteDraft };
      return s.companyId === getActiveCompanyId() ? { ...EMPTY_DRAFT, ...s.value } : null;
    } catch {
      return null;
    }
  },
  set(value: RouteDraft) {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ companyId: getActiveCompanyId(), value }));
    } catch {
      /* sem armazenamento: segue sem a recuperação */
    }
  },
  clear() {
    try {
      sessionStorage.removeItem(DRAFT_KEY);
    } catch {
      /* nada a fazer */
    }
  },
};

let memoryDraft: { owner: string; value: RouteDraft } | null = null;

/**
 * Rascunho só na MEMÓRIA do app (CaféGestor Vendedor V1): sobrevive à troca de aba dentro do
 * app, nunca é gravado no aparelho e pertence ao usuário e à empresa que o fizeram.
 */
export function memoryDraftStore(owner: string): DraftStore {
  return {
    get: () => (memoryDraft?.owner === owner ? { ...EMPTY_DRAFT, ...memoryDraft.value } : null),
    set: (value) => {
      memoryDraft = { owner, value };
    },
    clear: () => {
      if (memoryDraft?.owner === owner) memoryDraft = null;
    },
  };
}

/** Apaga o rascunho em memória (saída do usuário, troca de empresa). */
export function clearRouteMemory() {
  memoryDraft = null;
}

/** Conferência da nota no servidor (mesmo motor da emissão), só com conexão e NF-e escolhida. */
export function useRoutePreview(body: RouteSaleInput | null, enabled: boolean) {
  const signature = body ? JSON.stringify({ ...body, deviceId: "", previewHash: null }) : "";
  return useQuery({
    queryKey: ["rota", "previa", signature],
    enabled: enabled && body !== null,
    queryFn: () =>
      apiFetch<FiscalPreview>("/fiscal/rota/previa", {
        method: "POST",
        body: { ...body!, emit: true, previewHash: null },
        idempotencyKey: newIdempotencyKey(),
      }),
    staleTime: 60_000,
    retry: false,
  });
}
